import { ApiClientWrapper } from '@spike/api-client';
import { NewAppointment } from '@spike/new-booking-model';
import {
    resolveDiscountPayloadForSave,
    resolveOrderItemPriceForSave,
    shouldApplyMembershipRedeemOnSave
} from 'components/CreateBooking/utils/membershipDisplayPrice';
import { Transportation } from 'model/NewMultipleBooking';
import { Moment } from 'moment-timezone';
import { buildTransportationVariableDataPerSlot } from 'utils/transportationBulkSlotBuilder';
import { normalizeTransportationForSlotSave } from 'utils/transportationDateSlots';

export interface BuildTransportationAppointmentsParams {
    apiClientWrapper: ApiClientWrapper;
    bookingId: number;
    transportation: Transportation;
    petId: number;
    customerId: number;
    createdByStaffId: number;
    fallbackDate: Moment;
    timezone: string;
    status: number;
    marketplaceAddressId: number;
}

export const buildTransportationAppointments = ({
    apiClientWrapper,
    bookingId,
    transportation,
    petId,
    customerId,
    createdByStaffId,
    fallbackDate,
    timezone,
    status,
    marketplaceAddressId
}: BuildTransportationAppointmentsParams): Array<NewAppointment> => {
    const normalized = normalizeTransportationForSlotSave(transportation, timezone);
    const fallbackInTz = fallbackDate.clone().tz(timezone);
    const variableDataPerSlot = buildTransportationVariableDataPerSlot(normalized, fallbackInTz, timezone, false);

    return variableDataPerSlot.map(variableData => {
        const membershipDiscount = resolveDiscountPayloadForSave(variableData.itemData?.discount, variableData.service);
        const enrollmentId = shouldApplyMembershipRedeemOnSave(variableData.service)
            ? variableData.service?.enrollment?.id
            : undefined;
        const dateToSave = variableData.date!.clone().tz(timezone).seconds(0).milliseconds(0);
        const dateEndToSave = variableData.dateEnd
            ? variableData.dateEnd.clone().tz(timezone).seconds(0).milliseconds(0)
            : dateToSave.clone().add(variableData.service?.durationInMinutes || 0, 'minutes');
        const isPickup = variableData.itemData?.type === 'pickup';

        return {
            booking_id: bookingId,
            uuid: apiClientWrapper.uuid(),
            address_id: marketplaceAddressId,
            booked_at: dateToSave,
            booking_end_at: dateEndToSave,
            customer_id: customerId,
            pet_id: petId,
            service_id: variableData.service?.service.id!,
            staff_id: variableData.staffId,
            created_by: createdByStaffId,
            notes: '',
            order_item_price: resolveOrderItemPriceForSave(variableData.service),
            status,
            discount_amount: membershipDiscount?.amount,
            discount_percentage: membershipDiscount?.percentage,
            details: {
                transportation: {
                    type: isPickup ? 'pickup' : 'dropoff',
                    vehicleId: variableData.service?.vehicle?.id || 0,
                    address: variableData.service?.address || '',
                    instructions: variableData.service?.instructions || ''
                }
            },
            ...(enrollmentId != null
                ? {
                      redeemType: 'Enrollment' as const,
                      redeemId: enrollmentId
                  }
                : {})
        };
    });
};
